import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { safeInternalPath } from "../utils/navigation";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, logInWithEmailAndPassword } from "../services/firebase";
import LandingNavbar from "../components/landingNavbar";

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTarget = safeInternalPath(searchParams.get("redirect"));
  const registerTo = redirectTarget
    ? `/register?redirect=${encodeURIComponent(redirectTarget)}`
    : "/register";

  useEffect(() => {
    if (loading) return;
    if (user) {
      if (redirectTarget) {
        navigate(redirectTarget, { replace: true });
      } else {
        navigate("/my-designs");
      }
    }
  }, [user, loading, navigate, redirectTarget]);

  // Match DrillFlo onboarding: Fraunces + Geist (see DrillFloOnboardingExperience.tsx)
  useEffect(() => {
    const fraunces = document.createElement("link");
    fraunces.rel = "stylesheet";
    fraunces.href =
      "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400&display=swap";
    document.head.appendChild(fraunces);

    const style = document.createElement("style");
    style.setAttribute("data-drillflo-login-fonts", "true");
    style.textContent = `
@font-face {
  font-family: 'Geist';
  src: url('https://cdn.jsdelivr.net/npm/geist@1.3.1/dist/fonts/geist-sans/Geist-Variable.woff2') format('woff2');
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}
`;
    document.head.appendChild(style);

    return () => {
      fraunces.remove();
      style.remove();
    };
  }, []);

  const handleLogin = (e) => {
    e.preventDefault(); // Prevent page refresh
    logInWithEmailAndPassword(email, password);
  };

  return (
    <div className="min-h-screen bg-flo-canvas pt-20 font-geist text-slate-900 antialiased">
      <LandingNavbar />
      <section className="bg-flo-canvas">
        <div className="lg:grid lg:min-h-[calc(100vh-5rem)] lg:grid-cols-12">
          <section className="relative flex h-32 items-end overflow-hidden bg-slate-900 lg:col-span-5 lg:h-full xl:col-span-6 lg:rounded-2xl">
            <img
              alt=""
              src="./assets/images/bg-login.jpg"
              className="absolute inset-0 h-full w-full object-cover opacity-75"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-flo-blue/80 via-flo-blue/55 to-slate-900/75" />
            <div className="hidden lg:relative lg:block lg:p-12 text-left">
              <p className="font-fraunces text-lg font-semibold tracking-tight text-white">
                Drill<span className="text-flo-blue">Flo</span>
              </p>
              <h2 className="mt-6 font-fraunces text-2xl font-semibold tracking-tight text-white sm:text-3xl md:text-[2rem]">
                Welcome back
              </h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-white/90">
                Log in to your account, or use Sign up to join.
              </p>
            </div>
          </section>

          <main className="flex items-center justify-center px-8 py-10 sm:px-12 lg:col-span-7 lg:px-16 lg:py-12 xl:col-span-6">
            <div className="w-full max-w-md">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.05 }}
                className="w-full space-y-8 rounded-2xl border border-slate-200/90 bg-white p-8 shadow-[0_1px_2px_rgba(15,23,42,0.06)] md:p-10"
              >
                <div>
                  <h2 className="text-center font-fraunces text-3xl font-semibold tracking-tight text-slate-900 sm:text-[2rem]">
                    Log in
                  </h2>
                  <p className="mt-3 text-center text-base text-slate-600">
                    Or{" "}
                    <Link
                      to={registerTo}
                      className="font-medium text-flo-blue transition hover:text-flo-blue-hover"
                    >
                      sign up
                    </Link>
                  </p>
                </div>

                <form onSubmit={handleLogin} className="mt-2 space-y-6">
                  <div className="text-left">
                    <label
                      htmlFor="UserEmail"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="UserEmail"
                      placeholder="you@school.edu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      className="mt-2 block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 font-geist text-base text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-flo-blue focus:ring-[3px] focus:ring-[rgba(46,91,255,0.22)] sm:text-sm"
                      required
                    />
                  </div>

                  <div className="text-left">
                    <label
                      htmlFor="UserPassword"
                      className="block text-sm font-medium text-slate-700"
                    >
                      Password
                    </label>
                    <input
                      type="password"
                      id="UserPassword"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      className="mt-2 block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 font-geist text-base text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-flo-blue focus:ring-[3px] focus:ring-[rgba(46,91,255,0.22)] sm:text-sm"
                      required
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <a
                        href="/reset-password"
                        className="font-medium text-flo-blue transition hover:text-flo-blue-hover"
                      >
                        Forgot your password?
                      </a>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full rounded-xl bg-flo-blue px-6 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-flo-blue-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-flo-blue focus-visible:ring-offset-2 focus-visible:ring-offset-white active:scale-[0.97] motion-reduce:active:scale-100"
                  >
                    Sign in
                  </button>
                </form>
              </motion.div>
            </div>
          </main>
        </div>
      </section>
    </div>
  );
}

export default LoginPage;
